import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';

const DetailContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #E5E7EB;
`;

const CompletenessCircle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`;

const CircularProgress = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  margin-bottom: 1rem;
`;

const ProgressText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  font-weight: 700;
  color: #10B981;
`;

const ProgressLabel = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  text-align: center;
`;

const SnapshotSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SnapshotItem = styled.div`
  background: #f3f5f7;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 1rem;
`;

const SnapshotLabel = styled.div`
  font-size: 0.75rem;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const SnapshotValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const KeySignalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const KeySignalItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #F0FDF4;
  border-left: 3px solid #10B981;
  border-radius: 4px;
  
  &::before {
    content: '✓';
    color: #10B981;
    font-weight: 700;
    flex-shrink: 0;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
`;

const InfoContent = styled.div`
  background: #f3f5f7;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 1rem;
  
  p {
    margin: 0 0 0.5rem 0;
    color: #374151;
    line-height: 1.6;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    color: #374151;
  }
  
  li {
    margin: 0.25rem 0;
    line-height: 1.6;
  }
  
  h3 {
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
    margin: 1rem 0 0.5rem 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
`;

const ConfidenceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #10B981;
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 24px;
  font-weight: 600;
  font-size: 1rem;
`;

const ClaimUnderstandingDetail = ({ card }) => {
  if (!card) {
    return (
      <Card>
        <p>No card data available.</p>
      </Card>
    );
  }

  const percentage = parseInt(card.metric) || 100;
  const circumference = 2 * Math.PI * 85;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <DetailContainer>
      <MainContent>
        {/* Completeness Score */}
        <Card>
          <CardTitle>Data Completeness Score</CardTitle>
          <CompletenessCircle>
            <CircularProgress>
              <svg width="180" height="180" style={{ transform: 'rotate(-90deg)' }}>
                <circle
                  cx="90"
                  cy="90"
                  r="85"
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="12"
                />
                <circle
                  cx="90"
                  cy="90"
                  r="85"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="12"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                />
              </svg>
              <ProgressText>{card.metric}</ProgressText>
            </CircularProgress>
            <ProgressLabel>{card.description}</ProgressLabel>
          </CompletenessCircle>
        </Card>

        {/* Claim Snapshot */}
        <Card>
          <CardTitle>Structured Claim Snapshot</CardTitle>
          <SnapshotSection>
            <SnapshotItem>
              <SnapshotLabel>Patient Name</SnapshotLabel>
              <SnapshotValue>John Doe</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Claim ID</SnapshotLabel>
              <SnapshotValue>CLM-ID-0001</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Admission Date</SnapshotLabel>
              <SnapshotValue>15 January 2024</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Discharge Date</SnapshotLabel>
              <SnapshotValue>18 January 2024</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Primary Diagnosis</SnapshotLabel>
              <SnapshotValue>Acute Appendicitis</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Procedure</SnapshotLabel>
              <SnapshotValue>Appendectomy</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Hospital</SnapshotLabel>
              <SnapshotValue>City General Hospital</SnapshotValue>
            </SnapshotItem>
            <SnapshotItem>
              <SnapshotLabel>Total Claimed</SnapshotLabel>
              <SnapshotValue>IDR 127,800</SnapshotValue>
            </SnapshotItem>
          </SnapshotSection>
        </Card>

        {/* Detailed Information */}
        {card.moreInformation && (
          <Card>
            <CardTitle>Processing Details</CardTitle>
            
            {card.moreInformation.sourceInput && (
              <InfoSection>
                <InfoTitle>Source Input</InfoTitle>
                <InfoContent>
                  <ReactMarkdown>{card.moreInformation.sourceInput}</ReactMarkdown>
                </InfoContent>
              </InfoSection>
            )}

            {card.moreInformation.logic && (
              <InfoSection>
                <InfoTitle>Validation Logic</InfoTitle>
                <InfoContent>
                  <ReactMarkdown>{card.moreInformation.logic}</ReactMarkdown>
                </InfoContent>
              </InfoSection>
            )}

            {card.moreInformation.output && (
              <InfoSection>
                <InfoTitle>Output Determination</InfoTitle>
                <InfoContent>
                  <ReactMarkdown>{card.moreInformation.output}</ReactMarkdown>
                </InfoContent>
              </InfoSection>
            )}
          </Card>
        )}
      </MainContent>

      <Sidebar>
        {/* Status */}
        <Card>
          <CardTitle>Status</CardTitle>
          <p style={{ fontSize: '1rem', color: '#374151', margin: 0 }}>
            <strong>{card.subtitle}</strong>
          </p>
        </Card>

        {/* Key Signals */}
        {card.keySignalsText && (
          <Card>
            <CardTitle>Key Signals</CardTitle>
            <KeySignalsList>
              {card.keySignalsText.split('\n').filter(s => s.trim()).map((signal, index) => (
                <KeySignalItem key={index}>
                  {signal.replace(/^-\s*/, '').replace(/^✅\s*/, '')}
                </KeySignalItem>
              ))}
            </KeySignalsList>
          </Card>
        )}

        {/* Confidence */}
        {card.confidencePercent && (
          <Card>
            <CardTitle>Confidence</CardTitle>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
              <ConfidenceBadge>
                <span>🎯</span>
                {card.confidencePercent}% Confidence
              </ConfidenceBadge>
            </div>
          </Card>
        )}
      </Sidebar>
    </DetailContainer>
  );
};

export default ClaimUnderstandingDetail;
